import { useState, useEffect, useCallback } from 'react';

// Storage keys
export const STORAGE_KEYS = {
  NOTES: 'glissando_notes',
  INTERVALS: 'glissando_intervals',
  SESSIONS: 'glissando_sessions',
  STREAK: 'glissando_streak',
  SETTINGS: 'glissando_settings',
  CURRENT_RANGE: 'glissando_range',
  ONBOARDING: 'glissando_onboarding_complete'
};

// Hook for localStorage with SSR safety
export function useLocalStorage(key, initialValue) {
  // Initialize state from localStorage
  const [storedValue, setStoredValue] = useState(() => {
    try {
      if (typeof window === 'undefined') {
        return initialValue;
      }
      const item = window.localStorage.getItem(key);
      if (item === null) {
        // Key doesn't exist, store the initial value
        window.localStorage.setItem(key, JSON.stringify(initialValue));
        return initialValue;
      }
      return JSON.parse(item);
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  // Update localStorage when value changes
  const setValue = useCallback((value) => {
    try {
      // Use functional update pattern to ensure we always have latest state
      setStoredValue(prevValue => {
        const valueToStore = value instanceof Function ? value(prevValue) : value;
        
        // Persist to localStorage
        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }
        
        return valueToStore;
      });
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key]);

  // Sync with localStorage on mount and when storage changes in other tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue));
        } catch (error) {
          console.error(`Error parsing storage event for "${key}":`, error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [key]);

  return [storedValue, setValue];
}

// Initial note data structure — centered on middle C, A3–G4
const INITIAL_NOTE_RANGE = ['A3', 'B3', 'C4', 'D4', 'E4', 'F4', 'G4'];

// All available notes for future expansion
const ALL_NOTES = [
  'C2', 'D2', 'E2', 'F2', 'G2', 'A2', 'B2',
  'C3', 'D3', 'E3', 'F3', 'G3', 'A3', 'B3',
  'C4', 'D4', 'E4', 'F4', 'G4', 'A4', 'B4',
  'C5', 'D5', 'E5', 'F5', 'G5', 'A5', 'B5',
  'C6'
];

// Ordered unlock sequence following piano pedagogy.
// Tier 1 (first 2): C octave landmark anchors — clef balance guard suspended.
// Tier 2+: balanced expansion, |trebleCount - bassCount| <= 2 at all times.
// Starting range is A3–G4 (T=5, B=2); sequence brings full range to T=15, B=14.
export const PEDAGOGY_UNLOCK_SEQUENCE = [
  // Tier 1: C octave landmark anchors
  'C5', 'C3',
  // Tier 2: Balanced expansion outward from the starting range
  'F3', 'G3', 'A4', 'D3', 'B4', 'E3', 'D5',
  'G2', 'E5', 'F2', 'F5', 'E2', 'G5',
  'D2', 'A5', 'C2', 'B5', 'A2', 'C6', 'B2'
];

// Initialize note stats
export function createInitialNoteStats(notes = INITIAL_NOTE_RANGE) {
  const stats = {};
  notes.forEach(note => {
    stats[note] = {
      correctCount: 0,
      incorrectCount: 0,
      currentFrequency: 1, // 1 = normal frequency, decreases as mastered
      lastShownTimestamp: null,
      responseTimes: [], // last 10 response times in ms
      masteryTier: 0 // 0=new, 1=learning, 2=familiar, 3=mastered
    };
  });
  return stats;
}

// Default settings
export const DEFAULT_SETTINGS = {
  showNoteNames: false,
  soundEnabled: true,
  learningMode: false,
  sessionMode: 'count' // 'timed' or 'count'
};

// Default streak data
export const DEFAULT_STREAK = {
  currentCount: 0,
  lastSessionDate: null
};

// Default range
export const DEFAULT_RANGE = {
  min: 'A3',
  max: 'G4',
  unlockedNotes: INITIAL_NOTE_RANGE
};

// Mastery thresholds
export const MASTERY_CONFIG = {
  masteryAttempts: 8, // attempts needed for mastery
  masteryAccuracy: 1.0, // 100% accuracy required
  tierThresholds: [0, 3, 5, 8], // correct counts to reach each tier (0=new, 1=learning, 2=familiar, 3=mastered)
  frequencyReduction: [1, 0.75, 0.5, 0.2], // frequency at each tier
  minFrequency: 0.2 // floor for mastered notes
};

// Calculate mastery tier based on attempts and accuracy
// Mastered (tier 3) = 8 attempts with 100% accuracy
export function getMasteryTier(correctCount, incorrectCount) {
  const totalAttempts = correctCount + incorrectCount;
  
  // Mastered: 8+ attempts with 100% accuracy (no incorrect answers)
  if (totalAttempts >= MASTERY_CONFIG.masteryAttempts && incorrectCount === 0) {
    return 3; // Mastered
  }
  
  // For other tiers, use correct count thresholds
  // But cap at tier 2 (familiar) if there are any incorrect answers
  const thresholds = MASTERY_CONFIG.tierThresholds;
  
  for (let i = Math.min(thresholds.length - 1, incorrectCount > 0 ? 2 : 3); i >= 0; i--) {
    if (correctCount >= thresholds[i]) {
      return i;
    }
  }
  return 0;
}

// Get frequency for a note based on its mastery
export function getNoteFrequency(noteStats) {
  const tier = getMasteryTier(noteStats.correctCount, noteStats.incorrectCount);
  return MASTERY_CONFIG.frequencyReduction[tier];
}

// Check if all notes in range are mastered
export function checkAllMastered(notesStats, unlockedNotes) {
  return unlockedNotes.every(note => {
    const stats = notesStats[note];
    if (!stats) return false;
    return getMasteryTier(stats.correctCount, stats.incorrectCount) >= 3;
  });
}

// Index in PEDAGOGY_UNLOCK_SEQUENCE where Tier 3 (outer extensions) begins
const TIER3_START_INDEX = 15;
// Average mastery threshold required across BOTH clefs before any Tier 3 note unlocks
const TIER3_MASTERY_THRESHOLD = 2.0; // "familiar" level (tier 2)

// Get the next single note to unlock based on the pedagogy sequence.
// Gates on average mastery tier >= 1.0 for the candidate's clef before unlocking.
// Landmarks (C5, C3) bypass the clef balance guard.
export function getNextNoteToUnlock(currentRange, notes) {
  const unlocked = currentRange.unlockedNotes;

  // Find first note in pedagogy sequence not yet unlocked
  const candidate = PEDAGOGY_UNLOCK_SEQUENCE.find(n => !unlocked.includes(n));
  if (!candidate) return null;

  const candidateIndex = PEDAGOGY_UNLOCK_SEQUENCE.indexOf(candidate);
  const isLandmark = candidateIndex < 2; // C5 and C3 bypass balance guard

  // Determine clef: treble = C4 and above, bass = B3 and below
  const candidateOctave = parseInt(candidate.slice(-1));
  const candidateClef = candidateOctave >= 4 ? 'treble' : 'bass';

  // Average mastery check across already-unlocked notes in the same clef
  const clefNotes = unlocked.filter(n => {
    const oct = parseInt(n.slice(-1));
    return candidateClef === 'treble' ? oct >= 4 : oct < 4;
  });
  if (clefNotes.length > 0) {
    const avgTier = clefNotes.reduce((sum, n) => {
      const s = notes[n];
      return sum + (s ? getMasteryTier(s.correctCount, s.incorrectCount) : 0);
    }, 0) / clefNotes.length;
    if (avgTier < 1.0) return null; // Clef not ready
  }

  // Tier 3 gate: both clefs must reach avg tier >= 2.0 before outer extensions unlock
  if (candidateIndex >= TIER3_START_INDEX) {
    const trebleNotes = unlocked.filter(n => parseInt(n.slice(-1)) >= 4);
    const bassNotes = unlocked.filter(n => parseInt(n.slice(-1)) < 4);

    const avgTreble = trebleNotes.length > 0
      ? trebleNotes.reduce((sum, n) => {
          const s = notes[n];
          return sum + (s ? getMasteryTier(s.correctCount, s.incorrectCount) : 0);
        }, 0) / trebleNotes.length
      : 0;

    const avgBass = bassNotes.length > 0
      ? bassNotes.reduce((sum, n) => {
          const s = notes[n];
          return sum + (s ? getMasteryTier(s.correctCount, s.incorrectCount) : 0);
        }, 0) / bassNotes.length
      : 0;

    if (avgTreble < TIER3_MASTERY_THRESHOLD || avgBass < TIER3_MASTERY_THRESHOLD) {
      return null; // Both clefs not ready for Tier 3
    }
  }

  // Clef balance guard (landmarks bypass)
  if (!isLandmark) {
    const trebleCount = unlocked.filter(n => parseInt(n.slice(-1)) >= 4).length;
    const bassCount = unlocked.length - trebleCount;
    const newT = trebleCount + (candidateClef === 'treble' ? 1 : 0);
    const newB = bassCount + (candidateClef === 'bass' ? 1 : 0);
    if (Math.abs(newT - newB) > 2) return null;
  }

  return { note: candidate, direction: candidateClef };
}

// Check and update streak
export function checkStreak(currentStreak, today = new Date()) {
  const todayStr = today.toISOString().split('T')[0];
  const lastDate = currentStreak.lastSessionDate;
  
  if (!lastDate) {
    // First session ever
    return {
      currentCount: 1,
      lastSessionDate: todayStr,
      isNewStreak: true,
      streakBroken: false
    };
  }
  
  if (lastDate === todayStr) {
    // Already practiced today
    return {
      ...currentStreak,
      isNewStreak: false,
      streakBroken: false
    };
  }
  
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStr = yesterday.toISOString().split('T')[0];
  
  if (lastDate === yesterdayStr) {
    // Streak continues
    return {
      currentCount: currentStreak.currentCount + 1,
      lastSessionDate: todayStr,
      isNewStreak: true,
      streakBroken: false
    };
  }
  
  // Streak broken - start fresh
  return {
    currentCount: 1,
    lastSessionDate: todayStr,
    isNewStreak: true,
    streakBroken: true
  };
}

// Export all notes for reference
export { ALL_NOTES, INITIAL_NOTE_RANGE };

// ========================================
// INTERVAL PRACTICE SUPPORT
// ========================================

// Available intervals for practice (basic set)
export const INTERVALS = ['2nd', '3rd', '4th', '5th', '6th', '7th', 'Octave'];

// Initialize interval stats
export function createInitialIntervalStats(intervals = INTERVALS) {
  const stats = {};
  intervals.forEach(interval => {
    stats[interval] = {
      correctCount: 0,
      incorrectCount: 0,
      currentFrequency: 1,
      lastShownTimestamp: null,
      responseTimes: [],
      masteryTier: 0
    };
  });
  return stats;
}

// Default interval state
export const DEFAULT_INTERVAL_STATE = createInitialIntervalStats();

// Calculate interval from two note positions
export function calculateInterval(note1, note2) {
  const noteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

  const getAbsolutePosition = (note) => {
    const noteName = note.slice(0, -1);
    const octave = parseInt(note.slice(-1));
    const nameIndex = noteNames.indexOf(noteName);
    if (nameIndex === -1) {
      console.warn('calculateInterval: unrecognized note name', note);
      return -1;
    }
    return octave * 7 + nameIndex;
  };
  
  const pos1 = getAbsolutePosition(note1);
  const pos2 = getAbsolutePosition(note2);

  if (pos1 === -1 || pos2 === -1) {
    return 'Unknown';
  }

  const diff = Math.abs(pos2 - pos1) + 1; // +1 because intervals are 1-indexed
  
  // Map difference to interval name
  const intervalMap = {
    1: 'Unison',
    2: '2nd',
    3: '3rd',
    4: '4th',
    5: '5th',
    6: '6th',
    7: '7th',
    8: 'Octave'
  };
  
  return intervalMap[diff] || `${diff}th`;
}

// Generate a random interval pair within the current note range
// Ensures both notes are displayable on the same staff (same clef)
export function generateIntervalPair(availableNotes, targetInterval = null) {
  const noteNames = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

  const getAbsolutePosition = (note) => {
    const noteName = note.slice(0, -1);
    const octave = parseInt(note.slice(-1));
    const nameIndex = noteNames.indexOf(noteName);
    if (nameIndex === -1) return -1;
    return octave * 7 + nameIndex;
  };
  
  // Determine clef for a note (treble for C4 and above, bass for B3 and below)
  const getClef = (note) => {
    const octave = parseInt(note.slice(-1));
    if (octave < 4) return 'bass';
    return 'treble';
  };
  
  // If no target interval, pick a random one
  const intervals = ['2nd', '3rd', '4th', '5th', '6th', '7th', 'Octave'];
  const interval = targetInterval || intervals[Math.floor(Math.random() * intervals.length)];
  
  // Convert interval to step difference
  const intervalDiffs = {
    '2nd': 1,
    '3rd': 2,
    '4th': 3,
    '5th': 4,
    '6th': 5,
    '7th': 6,
    'Octave': 7
  };
  
  const diff = intervalDiffs[interval];
  
  // Sort available notes by position
  const sortedNotes = [...availableNotes].sort((a, b) => getAbsolutePosition(a) - getAbsolutePosition(b));
  
  // Find valid pairs where both notes are in the same clef range
  const validPairs = [];
  
  for (let i = 0; i < sortedNotes.length; i++) {
    const lowerNote = sortedNotes[i];
    const lowerPos = getAbsolutePosition(lowerNote);
    const lowerClef = getClef(lowerNote);
    
    for (let j = i + 1; j < sortedNotes.length; j++) {
      const upperNote = sortedNotes[j];
      const upperPos = getAbsolutePosition(upperNote);
      const upperClef = getClef(upperNote);
      
      // Only accept pairs where both notes are in the same clef
      // OR both can be displayed on treble (C4 and above)
      const sameClef = lowerClef === upperClef || 
                       (lowerClef === 'treble' && upperClef === 'treble') ||
                       (lowerNote === 'C4' && upperClef === 'treble'); // C4 can be shown on treble with ledger line
      
      if (upperPos - lowerPos === diff && sameClef) {
        validPairs.push([lowerNote, upperNote]);
      }
    }
  }
  
  if (validPairs.length === 0) {
    // If no valid pairs for this interval, return null
    return null;
  }
  
  // Pick a random valid pair
  const pair = validPairs[Math.floor(Math.random() * validPairs.length)];
  
  return {
    lowerNote: pair[0],
    upperNote: pair[1],
    interval: interval
  };
}

